import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { ordersApi, filesApi, artworkJobsApi, invoiceSyncApi } from '../lib/api';
import { uploadCustomerArtwork, describeUploadError } from '../lib/uploadCustomerArtwork';
import { formatDate, formatDateTime } from '../lib/utils';
import { Header } from '../components/Header';
import { OrderStatusBadge } from '../components/StatusBadge';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { NCBCustomerStatus } from '../components/NCBClearanceSection';
import ProductConfigWizard from '../components/ProductConfigWizard';
import { ProductionDetailsSection } from '../components/ProductionDetailsSection';
import { UploadLinksSection } from '../components/UploadLinksSection';
import OrderArtworkTemplates from '../components/OrderArtworkTemplates';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
    ArrowLeft, 
    Upload,
    Music,
    FileText,
    Send,
    Pencil,
    Calendar,
    Disc,
    Palette,
    Download,
    Trash2,
    AlertCircle,
    CheckCircle2,
    Clock,
    Image as ImageIcon,
    FileImage,
    Loader2,
    Truck,
    Link
} from 'lucide-react';

// Artwork Job Status Badge Component (customer-friendly)
const ArtworkJobStatusBadge = ({ status }) => {
    const statusConfig = {
        'Open': { label: 'Uploaded', className: 'bg-blue-100 text-blue-700' },
        'Assigned': { label: 'In Progress', className: 'bg-amber-100 text-amber-700' },
        'In_Progress': { label: 'In Progress', className: 'bg-amber-100 text-amber-700' },
        'Awaiting_Approval': { label: 'Needs Your Approval', className: 'bg-purple-100 text-purple-700' },
        'Approved': { label: 'Approved', className: 'bg-emerald-100 text-emerald-700' },
        'Completed': { label: 'Completed', className: 'bg-emerald-100 text-emerald-700' },
    };
    
    const config = statusConfig[status] || { label: 'Missing', className: 'bg-red-100 text-red-700' };
    
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.className}`} data-testid="artwork-job-status-badge">
            {config.label}
        </span>
    );
};

// Helper to format file size
const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Vinyl spec options
const VINYL_FORMATS = ['12"', '10"', '7"'];
const VINYL_SPEEDS = ['33 RPM', '45 RPM'];
const VINYL_WEIGHTS = [140, 180];
const TEST_PRESSINGS_OPTIONS = [0, 5, 10];

/**
 * ReadOnlyProductSummary — family-aware read-only spec grid for the
 * customer's Product Selection card. Vinyl orders show press specs
 * (format/quantity/speed/color/weight/test pressings); CD/DVD/Blu-ray
 * orders show disc specs (format/quantity/packaging + any extra
 * bundled_options), skipping the vinyl-only defaults that may still
 * be present on legacy orders from before the family-aware fix.
 */
function ReadOnlyProductSummary({ vinylSpecs = {}, productType = 'vinyl', t }) {
    const family = (productType || 'vinyl').toLowerCase();
    const common = [
        { label: t('product.format', 'Format'), value: vinylSpecs.format, testId: 'vinyl-format' },
        { label: t('product.quantity', 'Quantity'), value: vinylSpecs.quantity, testId: 'vinyl-quantity' },
    ];

    let fields;
    if (family === 'vinyl') {
        fields = [
            ...common,
            { label: t('product.speed', 'Speed'), value: vinylSpecs.speed, testId: 'vinyl-speed' },
            { label: t('product.color', 'Color'), value: vinylSpecs.color || vinylSpecs.color_mode, testId: 'vinyl-color' },
            {
                label: t('product.weight', 'Vinyl Weight'),
                value: vinylSpecs.vinyl_weight_g
                    ? `${vinylSpecs.vinyl_weight_g}g`
                    : (vinylSpecs.vinyl_weight || null),
                testId: 'vinyl-weight',
            },
            {
                label: t('product.test.pressings', 'Test Pressings'),
                value: vinylSpecs.test_pressings !== undefined && vinylSpecs.test_pressings !== null
                    ? (parseInt(vinylSpecs.test_pressings) ? String(vinylSpecs.test_pressings) : t('product.test.pressings.none', 'None'))
                    : null,
                testId: 'test-pressings',
            },
        ];
    } else {
        const VINYL_ONLY_KEYS = new Set([
            'speed', 'color', 'color_mode', 'vinyl_weight', 'vinyl_weight_g', 'test_pressings',
            'sleeve_package', 'inner_sleeve', 'outer_protection', 'printing', 'spine',
            'center_label', 'runout_engraving', 'engraving_text',
        ]);
        const KNOWN = new Set(['format', 'quantity', 'jacket_style']);
        const extras = Object.entries(vinylSpecs)
            .filter(([k, v]) => (
                !k.startsWith('_')
                && !KNOWN.has(k)
                && !VINYL_ONLY_KEYS.has(k)
                && v !== null && v !== undefined && v !== ''
            ))
            .map(([k, v]) => ({
                label: k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                value: typeof v === 'object' ? JSON.stringify(v) : String(v),
                testId: `spec-${k}`,
            }));
        fields = [
            ...common,
            { label: t('product.packaging', 'Packaging'), value: vinylSpecs.jacket_style, testId: 'packaging' },
            ...extras,
        ];
    }

    const visible = fields.filter((f) => f.value !== null && f.value !== undefined && f.value !== '');

    if (visible.length === 0) {
        return (
            <p className="text-sm text-muted-foreground">
                {t('product.no.configuration', 'No product configuration saved yet.')}
            </p>
        );
    }

    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {visible.map((f) => (
                <div key={f.label}>
                    <p className="text-xs text-muted-foreground">{f.label}</p>
                    <p className="text-sm font-medium" data-testid={f.testId}>{f.value}</p>
                </div>
            ))}
        </div>
    );
}


export default function CustomerOrderDetailPage() {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    
    const [order, setOrder] = useState(null);
    const [files, setFiles] = useState([]);
    const [artworkJob, setArtworkJob] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Edit mode
    const [isEditing, setIsEditing] = useState(false);
    const [editTitle, setEditTitle] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editCatalogueNumber, setEditCatalogueNumber] = useState('');
    const [isSavingCatalogueNumber, setIsSavingCatalogueNumber] = useState(false);
    const [editVinylSpecs, setEditVinylSpecs] = useState({
        format: '12"',
        quantity: 100,
        speed: '33 RPM',
        color: 'Black',
        vinyl_weight_g: 140,
        test_pressings: 0
    });
    
    // Upload state
    const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
    const [uploadCategory, setUploadCategory] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [creditBlocked, setCreditBlocked] = useState(false);
    const [creditBlockReason, setCreditBlockReason] = useState('');
    
    // Artwork upload state
    const [isArtworkUploadDialogOpen, setIsArtworkUploadDialogOpen] = useState(false);
    const [artworkFiles, setArtworkFiles] = useState([]);
    const [isUploadingArtwork, setIsUploadingArtwork] = useState(false);
    const artworkInputRef = useRef(null);

    useEffect(() => {
        fetchData();
    }, [orderId]);

    const fetchData = async () => {
        try {
            const [orderRes, filesRes, artworkJobRes] = await Promise.all([
                ordersApi.getOne(orderId),
                ordersApi.getFiles(orderId),
                artworkJobsApi.getOrderJob(orderId).catch(() => ({ data: null })),
            ]);
            
            setOrder(orderRes.data);
            setFiles(filesRes.data);
            setArtworkJob(artworkJobRes.data);
            setEditTitle(orderRes.data.title);
            setEditDescription(orderRes.data.description);
            setEditCatalogueNumber(orderRes.data.catalogue_number || '');
            if (orderRes.data.vinyl_specs) {
                setEditVinylSpecs(orderRes.data.vinyl_specs);
            }
            
            // Check credit status for draft orders
            if (orderRes.data.status === 'Draft') {
                try {
                    const orgId = orderRes.data.organization_id || user?.active_company_id || user?.organization_id;
                    if (orgId) {
                        const creditRes = await invoiceSyncApi.getCreditStatus(orgId);
                        if (creditRes.data?.is_blocked) {
                            setCreditBlocked(true);
                            setCreditBlockReason(creditRes.data.block_reason || 'Credit limit exceeded');
                        }
                    }
                } catch {
                    // Not critical
                }
            }
        } catch (error) {
            if (error.response?.status === 403) {
                toast.error(t('common.access_denied', 'Access denied'));
            } else {
                toast.error(t('order.load_details_failed', 'Failed to load order details'));
            }
            navigate('/dashboard');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveEdit = async (specsFromWizard = null) => {
        try {
            const vinylSpecsToSave = specsFromWizard || editVinylSpecs;
            const response = await ordersApi.update(orderId, {
                title: editTitle,
                description: editDescription,
                vinyl_specs: vinylSpecsToSave,
            });
            setOrder(response.data);
            setEditVinylSpecs(vinylSpecsToSave);
            setIsEditing(false);
            toast.success(t('order.updated_toast', 'Order updated'));
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update order');
        }
    };

    const handleSaveCatalogueNumber = async (newValue) => {
        // Don't save if unchanged
        if (newValue === (order?.catalogue_number || '')) return;
        
        setIsSavingCatalogueNumber(true);
        try {
            const response = await ordersApi.update(orderId, {
                catalogue_number: newValue || null,
            });
            setOrder(response.data);
            toast.success(t('order.catalogue_updated_toast', 'Catalogue number updated'));
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update catalogue number');
            // Revert on error
            setEditCatalogueNumber(order?.catalogue_number || '');
        } finally {
            setIsSavingCatalogueNumber(false);
        }
    };

    const handleUploadFile = async () => {
        if (!selectedFile || !uploadCategory) return;
        
        setIsUploading(true);
        try {
            const response = await filesApi.upload(orderId, uploadCategory, selectedFile);
            setFiles([response.data, ...files]);
            setIsUploadDialogOpen(false);
            setSelectedFile(null);
            setUploadCategory('');
            toast.success(t('order.file_uploaded_toast', 'File uploaded'));
            
            // Refresh order data after file upload
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload file');
        } finally {
            setIsUploading(false);
        }
    };
    
    // Artwork upload handlers
    const handleArtworkFilesSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            setArtworkFiles(prev => [...prev, ...files]);
        }
    };
    
    const removeArtworkFile = (index) => {
        setArtworkFiles(prev => prev.filter((_, i) => i !== index));
    };
    
    const handleArtworkUpload = async () => {
        if (artworkFiles.length === 0) {
            toast.error(t('order.select_files_first', 'Please select files to upload'));
            return;
        }
        
        setIsUploadingArtwork(true);
        try {
            // Uses chunked S3 multipart for files > 40 MB so 282 MB
            // artwork doesn't hit the ingress body limit.
            const response = await uploadCustomerArtwork(orderId, artworkFiles);

            toast.success(response.job_created
                ? `Artwork uploaded! ${response.files.length} file(s) added.`
                : `${response.files.length} file(s) added to artwork.`
            );
            
            // Close dialog and reset
            setIsArtworkUploadDialogOpen(false);
            setArtworkFiles([]);
            
            // Refresh to get updated artwork job
            fetchData();
        } catch (error) {
            // Surface the actual error so customers know what happened
            // instead of seeing the previous generic "Failed to upload".
            toast.error(describeUploadError(error), { duration: 8000 });
        } finally {
            setIsUploadingArtwork(false);
        }
    };

    const handleSubmitOrder = async () => {
        if (!window.confirm('Submit this order? You won\'t be able to edit it after submission.')) return;
        
        setIsSubmitting(true);
        try {
            const response = await ordersApi.submit(orderId);
            // Show success message
            toast.success(t('order.submitted_toast', 'Order submitted successfully! Your order is now being processed.'), {
                duration: 4000
            });
            // Redirect to orders page after a short delay
            setTimeout(() => {
                navigate('/dashboard');
            }, 1500);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit order');
            setIsSubmitting(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditTitle(order.title);
        setEditDescription(order.description);
        if (order.vinyl_specs) {
            setEditVinylSpecs(order.vinyl_specs);
        }
    };

    const handleDownloadFile = async (file) => {
        try {
            const result = await filesApi.download(file.id);
            // If it's a local file (blob returned), handle download manually
            if (!result.isS3 && result.data) {
                const url = window.URL.createObjectURL(result.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', file.original_filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }
            // S3 files are handled by opening in new tab via filesApi.download
        } catch (error) {
            toast.error(t('artwork_review.download_failed', 'Failed to download file'));
        }
    };

    const handleDeleteFile = async (fileId) => {
        if (!window.confirm('Are you sure you want to delete this file?')) return;
        
        try {
            await filesApi.delete(fileId);
            setFiles(files.filter(f => f.id !== fileId));
            toast.success(t('order.file_deleted_toast', 'File deleted'));
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete file');
        }
    };

    const canEdit = order?.status === 'Draft';
    const canUploadFiles = order?.status && !['In Progress', 'Completed'].includes(order.status);
    const canDeleteFiles = canUploadFiles;

    const vinylSpecs = order?.vinyl_specs || { 
        format: '12"', 
        quantity: 100, 
        speed: '33 RPM', 
        color: 'Black',
        vinyl_weight_g: 140,
        test_pressings: 0
    };

    // Redirect ALL customer orders to the new Order Workspace
    // This hook must be before any conditional returns
    useEffect(() => {
        if (order) {
            navigate(`/orders/${orderId}/files`, { replace: true });
        }
    }, [order, orderId, navigate]);

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
                    <LoadingSpinner size="lg" />
                </div>
            </div>
        );
    }
    
    // Return null - order will redirect via useEffect above.
    // QuoteSection lives on OrderFilesPage now (where the redirect lands).
    if (!order) return null;
    return null;

    return (
        <div className="min-h-screen bg-background" data-testid="customer-order-detail-page">
            {user?.role === 'guest' ? (
                <header className="border-b border-border bg-white">
                    <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
                        <img src="/owntune-logo-dark.png" alt="Owntune" className="h-7 w-auto" />
                        <div className="text-right">
                            <p className="text-xs text-slate-500">{t('order.upload_guest', 'Upload guest')}</p>
                            {order?.organization_id && (
                                <p className="text-xs font-medium text-slate-700">
                                    Invited to upload to order #{order.order_number || ''}
                                </p>
                            )}
                        </div>
                    </div>
                </header>
            ) : (
                <Header />
            )}
            
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Back Button (hidden for guests — they're locked to this order) */}
                {user?.role !== 'guest' && (
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/dashboard')}
                        className="mb-6 -ml-2 text-slate-600 hover:text-slate-900"
                        data-testid="back-button"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to My Orders
                    </Button>
                )}

                {/* Order Header - Compact flat header */}
                <div className="mb-8">
                    <div className="flex items-start justify-between gap-8">
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                                <h1 
                                    className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight text-slate-900" 
                                    data-testid="order-title"
                                >
                                    {order.title}
                                </h1>
                                <div className="relative group">
                                    <OrderStatusBadge status={order.status} />
                                    {order.status === 'Shipping' && (
                                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-purple-700 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                            <Truck className="h-3 w-3 inline mr-1" />
                                            {t('order.shipping.tooltip', 'Your order is on its way!')}
                                            <div className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-purple-700" />
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* Artist (separate from title). Hidden on legacy
                                orders that never stored an artist. */}
                            {order.artist && order.artist.trim() && (
                                <p className="text-base text-slate-600 mb-1" data-testid="order-artist">
                                    <span className="text-slate-400">{t('artwork_share.artist_prefix', 'Artist:')}</span>{' '}
                                    <span className="font-medium">{order.artist}</span>
                                </p>
                            )}
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                {order.description && (
                                    <>
                                        <span data-testid="order-description">{order.description}</span>
                                        <span className="text-slate-300">•</span>
                                    </>
                                )}
                                <span className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {t('order.created', 'Created')} {formatDate(order.created_at)}
                                </span>
                                {order.delivery_date && (
                                    <>
                                        <span className="text-slate-300">•</span>
                                        <span className="flex items-center gap-1 text-purple-600 font-medium" data-testid="delivery-date">
                                            <Clock className="h-3 w-3" />
                                            {t('order.expected_delivery', 'Expected')} {formatDate(order.delivery_date)}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                        
                        {/* Catalogue Number - Editable field */}
                        <div className="flex-shrink-0 w-48">
                            <Label htmlFor="catalogue-number" className="text-xs font-medium text-slate-500 mb-1 block">
                                {t('order.catalogue_number', 'Catalogue No.')}
                            </Label>
                            <div className="relative">
                                <Input
                                    id="catalogue-number"
                                    value={editCatalogueNumber}
                                    onChange={(e) => setEditCatalogueNumber(e.target.value)}
                                    onBlur={(e) => handleSaveCatalogueNumber(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.target.blur();
                                        }
                                    }}
                                    placeholder="e.g. CAT-001"
                                    className="h-9 text-sm pr-8"
                                    disabled={!canEdit || isSavingCatalogueNumber}
                                    data-testid="catalogue-number-input"
                                />
                                {isSavingCatalogueNumber && (
                                    <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* NCB Clearance Status - Customer view (neutral message) */}
                <NCBCustomerStatus order={order} />

                {/* Product Selection Section */}
                <Card className="mb-6">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Disc className="h-5 w-5 text-slate-600" />
                            {t('product.selection.title', 'Product Selection')}
                        </CardTitle>
                        <CardDescription>
                            {t('product.selection.description', 'Configure your vinyl format, quantity, and specifications.')}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {/* Show wizard directly for Draft orders, or when editing */}
                        {(canEdit || isEditing) ? (
                            <ProductConfigWizard
                                initialSpecs={order?.vinyl_specs || editVinylSpecs}
                                onConfigChange={(newSpecs, priceEstimate) => {
                                    setEditVinylSpecs(newSpecs);
                                }}
                                onComplete={(newSpecs, priceEstimate) => {
                                    setEditVinylSpecs(newSpecs);
                                    handleSaveEdit(newSpecs);
                                    toast.success(t('order.config_saved_toast', 'Product configuration saved'));
                                }}
                                orderMetadata={{
                                    // Route the customer's org through the
                                    // shared backend pricing engine so the
                                    // Visual Builder shows the exact same
                                    // local-currency total as the review
                                    // page / quote PDF for this order.
                                    organization_id: order?.organization_id,
                                    product_type: order?.product_type,
                                    is_reorder: order?.is_reorder || order?.order_type === 'reorder',
                                }}
                            />
                        ) : (
                            /* Show read-only summary for non-draft orders */
                            <ReadOnlyProductSummary vinylSpecs={vinylSpecs} productType={order?.product_type} t={t} />
                        )}
                        
                        {/* Production Details - shows option attribute selections */}
                        {order?.specs?._option_attributes && Object.keys(order.specs._option_attributes).length > 0 && (
                            <ProductionDetailsSection 
                                optionAttributes={order.specs._option_attributes}
                                className="mt-4"
                            />
                        )}
                    </CardContent>
                </Card>

                {/* Audio Master Section */}
                <Card className="mb-6">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Music className="h-5 w-5 text-blue-500" />
                            Audio Master
                        </CardTitle>
                        <CardDescription>
                            Upload audio master files for your vinyl production
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {/* Audio files list */}
                        {files.filter(f => ['audio', 'audio_side_a', 'audio_side_b'].includes(f.category)).length > 0 ? (
                            <div className="space-y-2 mb-4">
                                {files.filter(f => ['audio', 'audio_side_a', 'audio_side_b'].includes(f.category)).map((file) => (
                                    <div 
                                        key={file.id} 
                                        className="flex items-center justify-between p-3 bg-background rounded-lg"
                                        data-testid={`audio-file-${file.id}`}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="flex-shrink-0 w-10 h-10 rounded-md bg-blue-100 flex items-center justify-center">
                                                <Music className="h-5 w-5 text-blue-500" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-slate-700 truncate">
                                                    {file.original_filename}
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    {formatFileSize(file.size)}
                                                    {file.uploaded_at && ` • ${formatDate(file.uploaded_at)}`}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDownloadFile(file)}
                                                className="h-8 w-8 p-0"
                                                data-testid={`download-audio-${file.id}`}
                                            >
                                                <Download className="h-4 w-4" />
                                            </Button>
                                            {canDeleteFiles && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDeleteFile(file.id)}
                                                    className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                                                    data-testid={`delete-audio-${file.id}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground mb-4">{t('order.no_audio_uploaded', 'No audio files uploaded yet')}</p>
                        )}
                        {canUploadFiles && (
                            <Button 
                                onClick={() => {
                                    setUploadCategory('audio');
                                    setIsUploadDialogOpen(true);
                                }}
                                className="bg-blue-600 hover:bg-blue-700"
                                data-testid="upload-audio-btn"
                            >
                                <Upload className="h-4 w-4 mr-2" />
                                Upload Audio Master
                            </Button>
                        )}
                    </CardContent>
                </Card>

                {/* Artwork Section */}
                <Card className={`mb-6 ${artworkJob?.status === 'Awaiting_Approval' ? 'border-purple-300 bg-purple-50/30' : ''}`}>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Palette className="h-5 w-5 text-purple-500" />
                            Artwork
                            <ArtworkJobStatusBadge status={artworkJob?.status} />
                        </CardTitle>
                        <CardDescription>
                            {artworkJob?.status === 'Awaiting_Approval' 
                                ? 'Artwork is ready for your review and approval'
                                : artworkJob 
                                    ? `${artworkJob.artwork_files?.length || 0} file(s) uploaded. Your artwork is being processed.`
                                    : 'Upload your artwork files to start the review process'
                            }
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {artworkJob?.status === 'Awaiting_Approval' ? (
                            <div className="space-y-3">
                                <div className="flex items-center gap-2 text-purple-700 bg-purple-100 p-3 rounded-lg">
                                    <AlertCircle className="h-5 w-5" />
                                    <span className="font-medium">{t('order.approval_needed', 'Your approval is needed')}</span>
                                </div>
                                <Button 
                                    onClick={() => navigate(`/orders/${orderId}/artwork-review`)}
                                    className="bg-purple-600 hover:bg-purple-700"
                                    data-testid="artwork-review-btn"
                                >
                                    <Palette className="h-4 w-4 mr-2" />
                                    Review Artwork
                                </Button>
                            </div>
                        ) : artworkJob?.status === 'Approved' || artworkJob?.status === 'Completed' ? (
                            <div className="flex items-center gap-2 text-emerald-600">
                                <CheckCircle2 className="h-5 w-5" />
                                <span>{t('order.artwork_approved', 'Artwork approved and ready for production')}</span>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {/* Templates for this order — reference files
                                    resolved from the SKUs used on the order.
                                    Downloading one does NOT count as uploaded
                                    artwork. */}
                                <OrderArtworkTemplates orderId={orderId} />

                                {/* Show uploaded files */}
                                {artworkJob?.artwork_files?.length > 0 && (
                                    <div className="space-y-2">
                                        <div className="text-sm font-medium text-slate-700">{t('order.uploaded_artwork_files', 'Uploaded artwork files:')}</div>
                                        <div className="space-y-1">
                                            {artworkJob.artwork_files.map((file, idx) => (
                                                <div key={file.id || idx} className="flex items-center gap-2 text-sm bg-background p-2 rounded">
                                                    <FileImage className="h-4 w-4 text-purple-500" />
                                                    <span className="flex-1 truncate">{file.filename || file.original_filename}</span>
                                                    <span className="text-xs text-slate-400">{formatFileSize(file.size)}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-slate-500 pt-1">
                                            <Clock className="h-4 w-4" />
                                            <span>{t('order.being_processed_artwork', 'Being processed by the artwork team')}</span>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Upload button */}
                                <Button 
                                    onClick={() => setIsArtworkUploadDialogOpen(true)}
                                    className="bg-purple-600 hover:bg-purple-700"
                                    data-testid="upload-artwork-btn"
                                >
                                    <Upload className="h-4 w-4 mr-2" />
                                    {artworkJob?.artwork_files?.length > 0 ? 'Upload More Artwork' : 'Upload Artwork'}
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Upload Links - Share with external partners (hidden for guests) */}
                {user?.role !== 'guest' && (
                    <Card className="mb-6">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Link className="h-5 w-5 text-violet-500" />
                                Upload Links
                            </CardTitle>
                            <CardDescription>
                                Share secure links with external partners (mastering studios, artwork vendors) to upload files directly to this order
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <UploadLinksSection orderId={orderId} />
                        </CardContent>
                    </Card>
                )}

                {/* Submit Button (hidden for guests) */}
                {user?.role !== 'guest' && order.status === 'Draft' && (
                    <Card className={creditBlocked ? "border-red-300 bg-red-50/50" : "border-indigo-200 bg-indigo-50/50"}>
                        <CardContent className="py-6">
                            {creditBlocked ? (
                                <div className="flex items-center gap-3" data-testid="credit-block-submit">
                                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                                    <div className="flex-1">
                                        <h3 className="font-medium text-red-800" style={{ fontFamily: 'Inter, sans-serif' }}>
                                            Order submission blocked
                                        </h3>
                                        <p className="text-sm text-red-600">
                                            {creditBlockReason}.{' '}
                                            <button onClick={() => navigate('/invoices')} className="underline font-medium hover:text-red-800">
                                                View invoices
                                            </button>
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-medium text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
                                            Ready to submit?
                                        </h3>
                                        <p className="text-sm text-muted-foreground">
                                            Once submitted, you won&apos;t be able to edit the order details.
                                        </p>
                                    </div>
                                    <Button
                                        onClick={handleSubmitOrder}
                                        disabled={isSubmitting}
                                        className="bg-indigo-600 hover:bg-indigo-700"
                                        data-testid="submit-order-button"
                                    >
                                        {isSubmitting ? (
                                            'Submitting...'
                                        ) : (
                                            <>
                                                <Send className="h-4 w-4 mr-2" />
                                                Submit Order
                                            </>
                                        )}
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
            </main>

            {/* Upload Dialog - Audio Master only */}
            <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Music className="h-5 w-5 text-blue-500" />
                            Upload Audio Master
                        </DialogTitle>
                        <DialogDescription>
                            Upload your audio master file for vinyl production
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div className="space-y-2">
                            <Label>{t('order.audio_file', 'Audio File')}</Label>
                            <Input
                                type="file"
                                ref={fileInputRef}
                                accept="audio/*,.wav,.mp3,.flac,.aiff"
                                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                                data-testid="audio-file-input"
                            />
                            {selectedFile && (
                                <p className="text-xs text-muted-foreground">
                                    Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                                </p>
                            )}
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                            <Button 
                                variant="outline" 
                                onClick={() => {
                                    setIsUploadDialogOpen(false);
                                    setSelectedFile(null);
                                    setUploadCategory('');
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleUploadFile}
                                disabled={isUploading || !selectedFile}
                                className="bg-blue-600 hover:bg-blue-700"
                                data-testid="confirm-audio-upload-btn"
                            >
                                {isUploading ? 'Uploading...' : 'Upload'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Artwork Upload Dialog */}
            <Dialog open={isArtworkUploadDialogOpen} onOpenChange={setIsArtworkUploadDialogOpen}>
                <DialogContent className="sm:max-w-lg overflow-hidden w-full">
                    <DialogHeader className="min-w-0">
                        <DialogTitle className="flex items-center gap-2">
                            <Palette className="h-5 w-5 text-purple-500" />
                            Upload Artwork
                        </DialogTitle>
                        <DialogDescription>
                            Upload your artwork files (images, PDFs) for review. Our team will process and prepare them for vinyl production.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4 min-w-0 w-full overflow-hidden">
                        {/* File input */}
                        <div className="space-y-2">
                            <Label>{t('order.select_files_label', 'Select Files')}</Label>
                            <div 
                                className="border-2 border-dashed border-purple-200 rounded-lg p-6 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50/50 transition-colors"
                                onClick={() => artworkInputRef.current?.click()}
                            >
                                <input
                                    type="file"
                                    ref={artworkInputRef}
                                    className="hidden"
                                    accept="image/*,.pdf,.ai,.eps,.psd"
                                    multiple
                                    onChange={handleArtworkFilesSelect}
                                    data-testid="artwork-file-input"
                                />
                                <ImageIcon className="h-10 w-10 mx-auto text-purple-400 mb-2" />
                                <p className="text-sm font-medium text-purple-600">{t('order.click_select_files', 'Click to select files')}</p>
                                <p className="text-xs text-slate-500 mt-1">{t('order.artwork_supports', 'Supports: JPG, PNG, PDF, AI, EPS, PSD')}</p>
                            </div>
                        </div>

                        {/* Selected files list */}
                        {artworkFiles.length > 0 && (
                            <div className="space-y-2 min-w-0">
                                <Label>Selected Files ({artworkFiles.length})</Label>
                                <div className="max-h-40 overflow-y-auto overflow-x-hidden space-y-1 min-w-0 w-full">
                                    {artworkFiles.map((file, idx) => (
                                        <div key={idx} className="flex items-center justify-between gap-2 p-2 bg-background rounded text-sm w-full min-w-0 overflow-hidden">
                                            <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
                                                <FileImage className="h-4 w-4 text-purple-500 flex-shrink-0" />
                                                <span className="truncate min-w-0 max-w-full block flex-1" title={file.name}>{file.name}</span>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                <span className="text-xs text-slate-400 whitespace-nowrap">{formatFileSize(file.size)}</span>
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    className="h-6 w-6 p-0 flex-shrink-0"
                                                    onClick={() => removeArtworkFile(idx)}
                                                >
                                                    <Trash2 className="h-3 w-3 text-red-500" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end gap-3 pt-4">
                            <Button 
                                variant="outline" 
                                onClick={() => {
                                    setIsArtworkUploadDialogOpen(false);
                                    setArtworkFiles([]);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleArtworkUpload}
                                disabled={isUploadingArtwork || artworkFiles.length === 0}
                                className="bg-purple-600 hover:bg-purple-700"
                                data-testid="confirm-artwork-upload-btn"
                            >
                                {isUploadingArtwork ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="h-4 w-4 mr-2" />
                                        Upload {artworkFiles.length} File{artworkFiles.length !== 1 ? 's' : ''}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
